import { useQuery } from "@apollo/client";
import { ReactElement, useState } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  ComplianceBundleSortBy,
  ListComplianceBundlesQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import PageContainer from "~frontend/components/PageContainer";
import { TableBox, TableControls } from "~frontend/components/Table";
import { LIST_COMPLIANCE_BUNDLES } from "~frontend/graphql/complianceBundles";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";

import { BundleDialog } from "./BundleDialog";
import BundleTable from "./BundleTable";

export const BUNDLE_SORTS = [
  {
    value: "CREATED_AT_DESC",
    label: "Created At, Newest First",
  },
  {
    value: "CREATED_AT_ASC",
    label: "Created At, Oldest First",
  },
  {
    value: "NAME_ASC",
    label: "Name, A-Z",
  },
  {
    value: "NAME_DESC",
    label: "Name, Z-A",
  },
] as const;

export const ComplianceBundles = (): ReactElement => {
  useBreadcrumbs([
    {
      label: "Compliance Bundles",
      path: "/staff/compliance-bundles",
    },
  ]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const [bundleStatusFilter, setBundleStatusFilter] = useQueryParam(
    "bundleStatus",
    withDefault(StringParam, "active")
  );

  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, BUNDLE_SORTS[0].value)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const listVariables = {
    input: {
      sort: sort as ComplianceBundleSortBy,
      filter: { stringContains: search, status: bundleStatusFilter },
      limit: pageSize,
      offset: page * pageSize,
    },
  };

  const { loading, error, data, refetch } =
    useQuery<ListComplianceBundlesQuery>(LIST_COMPLIANCE_BUNDLES, {
      variables: listVariables,
    });

  const [bundleDialogOpen, toggleBundleDialog] = useState(false);

  return (
    <PageContainer maxWidth="lg">
      <BundleDialog
        open={bundleDialogOpen}
        onClose={async () => {
          toggleBundleDialog(false);
          await refetch();
        }}
      />

      <TableControls
        addButtons={[
          {
            label: "Add Bundle",
            onClick: () => toggleBundleDialog(true),
            id: "add-bundle",
          },
        ]}
        filters={[
          {
            id: "status",
            label: "Bundle Status",
            value: bundleStatusFilter as string,
            onChange: setBundleStatusFilter,
            options: ["Active", "Inactive"].map(status => ({
              value: status.toLowerCase(),
              label: status,
            })),
          },
        ]}
        sort={{
          value: sort,
          onChange: setSort,
          options: BUNDLE_SORTS.map(sort => ({
            value: sort.value,
            label: sort.label,
          })),
        }}
        search={{
          value: search,
          onChange: value => {
            setSearch((value as string).toLowerCase());
            setPage(0);
          },
          placeholder:
            "Search for a 10DLC Campaign ID, Org Legal Name, Bundle Slug, or Tenant Slug...",
        }}
      />
      <TableBox sx={{ pb: 1 }}>
        <AsyncWrapper data={data} errors={error} loading={loading}>
          {({ listComplianceBundles }) => {
            return (
              <BundleTable
                rows={listComplianceBundles.bundles}
                handleChangePage={setPage}
                handleChangeRowsPerPage={newRowsPerPage => {
                  setPageSize(newRowsPerPage);
                  setPage(0);
                }}
                rowsPerPage={pageSize}
                page={page}
                totalCount={listComplianceBundles.totalCount}
              />
            );
          }}
        </AsyncWrapper>
      </TableBox>
    </PageContainer>
  );
};
